import { domain } from "../util/Apiconfig";
import { getUserToken } from "../function/AuthStorage";
import { SCOPE_HEADER, scopeHeaderValue } from "./scopeSelection";

/**
 * العقد النشط — مخزن خارج React عمداً.
 *
 * مُعترِض axios ليس مكوّناً، فلا يقرأ حالة React. ولو قرأ العقد من
 * `localStorage` في كل طلب لصار كل نداء قراءةً من القرص. فالقيمة تُحفظ هنا
 * في الذاكرة، ويكتبها المزوّد مرّة عند كل تغيير.
 *
 * وما يُرسَل من هنا **مُدَّعى** لا مُسلَّم به: الخادم يتحقّق منه في كل طلب،
 * ويردّه إلى عقد المستخدم إن لم يكن مصرّحاً به. فالعبث بالقيمة في المتصفّح
 * لا يفتح عقداً.
 */

export const ACTIVE_CONTRACT_HEADER = "X-Contract-Id";

const STORAGE_KEY = "asf.activeContractId";

let activeContractId = null;
const listeners = new Set();

/** يُقرأ المحفوظ مرّة عند الإقلاع. */
export function readStoredContractId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    // التخزين قد يكون مقفلاً (تصفّح خاص) — والعقد النشط ليس سبباً لتعطيل
    // التطبيق: بلا محفوظ يختار الخادم عقد المستخدم الوحيد.
    return null;
  }
}

export function getActiveContractId() {
  return activeContractId;
}

export function setActiveContractId(id) {
  const next = Number.isInteger(id) && id > 0 ? id : null;
  if (next === activeContractId) return;

  activeContractId = next;

  try {
    if (next === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // الحفظ تحسينٌ لا شرط: يضيع الاختيار عند إعادة التحميل وحسب.
  }

  listeners.forEach((fn) => fn(next));
}

export function subscribeActiveContract(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * هل هذا الطلب ذاهب إلى خادمنا؟
 *
 * الترويسة لا تُرسَل إلى أي عنوان آخر: إرسال معرّف عقد إلى طرف ثالث تسريبٌ
 * لبنية بياناتنا، ولو كان رقماً.
 */
function isOwnApi(config) {
  const url = config?.url ?? "";

  // نسبيّ = على أصل الصفحة نفسه.
  if (!/^https?:\/\//i.test(url)) {
    const base = config?.baseURL ?? "";
    if (!/^https?:\/\//i.test(base)) return true;
    return domain === "" || base.startsWith(domain);
  }

  return domain !== "" && url.startsWith(domain);
}

/**
 * يُلحق ترويسة العقد بكل طلب من هذا المثيل.
 *
 * تُسجَّل على المثيل المشترك **وعلى axios العام**: مثيلات `axios.create`
 * لا ترث مُعترِضات المثيل الافتراضي، وأكثر من خمسين ملفاً في المشروع تنادي
 * `axios` مباشرة. فتسجيلٌ واحد كان سيغطّي نصف النداءات ويترك النصف الآخر
 * بلا عقد — وهو أسوأ من لا شيء، إذ يبدو النظام مقيَّداً وهو ليس كذلك.
 */
export function attachContractHeader(instance) {
  instance.interceptors.request.use((config) => {
    if (!isOwnApi(config)) return config;

    config.headers = config.headers ?? {};

    const id = getActiveContractId();
    if (id) config.headers[ACTIVE_CONTRACT_HEADER] = String(id);

    // شريط النطاق العام (GET فقط: القراءة تتضيّق، والكتابة لا تتأثر)
    const scopeValue = scopeHeaderValue();
    if (scopeValue && (config.method ?? "get").toLowerCase() !== "delete") config.headers[SCOPE_HEADER] = scopeValue;

    // مصدر العملية لسجل أحداث أمر العمل: الصفحة التي جرى منها التعديل
    const origin = currentOrigin();
    if (origin && !config.headers["X-ASF-Origin"]) config.headers["X-ASF-Origin"] = origin;

    /*
      رمز الدخول يُلحق هنا أيضاً.

      كان على المثيل المشترك وحده، و٣١ ملفاً ينادي axios الخام بلا ترويسة
      رمز إطلاقاً. وكانت تعمل لأن الخادم كان يقبل الطلب بلا مصادقة — فلمّا
      صارت المصادقة مطلوبة افتراضياً ظهرت كلّها ٤٠١.

      أي أن تلك الشاشات لم تكن «مصرّحاً لها»، بل كانت تعمل لأن لا أحد سأل.
      وإلحاق الرمز في مكان واحد يعني أن لا مسار يُنسى.

      وما كان له رمز في الإعدادات يُترك: قد يكون مقصوداً.
    */
    // ترويسة صريحة بلا رمز فعلي («Bearer undefined» من userData?.token أو
    // من مفتاح تخزين لا وجود له) تُعامل كأنها غائبة، وإلا أُرسلت كما هي وردّ
    // الخادم 401 مع أن المستخدم مسجّل الدخول.
    const explicit = config.headers.Authorization ?? config.headers.authorization;
    if (typeof explicit === "string" && /^Bearer\s*(undefined|null)?\s*$/i.test(explicit)) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }

    if (!config.headers.Authorization && !config.headers.authorization) {
      const token = getUserToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  return instance;
}

/** صفحة مصدر العملية (تُسجَّل مع كل حدث في سجل أمر العمل). */
const ORIGINS = [
  [/^\/daily-execution/, "daily-execution"],
  [/^\/project\//, "work-order-details"],
  [/^\/work-orders\/new/, "work-order-create"],
  [/^\/execution-tracking/, "execution-tracking"],
  [/^\/monitoring/, "monitoring"],
  [/^\/search-requests/, "search"],
  [/^\/deleted-projects/, "trash"],
  [/^\/contract-settings/, "contract-settings"],
  [/^\/ai-assistant/, "ai-assistant"],
];

export function currentOrigin() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "";
  const hit = ORIGINS.find(([re]) => re.test(path));
  return hit ? hit[1] : null;
}
